/**
 * Location Storage - IndexedDB storage for GPS positions
 *
 * Provides persistent local storage for GPS positions with:
 * - Efficient time-range queries via timestamp index
 * - LTTB downsampling for historical queries
 * - Automatic cleanup of old positions
 */

import { DebugLogger } from './debugLogger';

export interface GeoPosition {
    latitude: number;
    longitude: number;
    accuracy: number;
    timestamp: number;
    altitude?: number;
    speed?: number;
    heading?: number;
}

interface StoredPosition extends GeoPosition {
    id?: number;  // Auto-incremented by IndexedDB
}

const DB_NAME = 'editoor-location-db';
const DB_VERSION = 1;
const STORE_NAME = 'positions';

export class LocationStorage {
    private db: IDBDatabase | null = null;
    private isOpen = false;

    /**
     * Open the IndexedDB database
     */
    async open(): Promise<void> {
        if (this.isOpen && this.db) {
            return;
        }

        return new Promise((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to open location database', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                const db = request.result;
                this.db = db;
                this.isOpen = true;
                // Another connection wants to delete (storage reset) or upgrade:
                // step aside so it is not blocked, and reopen on next use.
                db.onversionchange = () => {
                    db.close();
                    if (this.db === db) {
                        this.db = null;
                        this.isOpen = false;
                    }
                };
                DebugLogger.log('init', 'Location database opened');
                resolve();
            };

            request.onupgradeneeded = (event) => {
                const db = (event.target as IDBOpenDBRequest).result;

                // Create positions store with auto-incrementing key
                if (!db.objectStoreNames.contains(STORE_NAME)) {
                    const store = db.createObjectStore(STORE_NAME, {
                        keyPath: 'id',
                        autoIncrement: true
                    });

                    // Create index on timestamp for efficient range queries
                    store.createIndex('timestamp', 'timestamp', { unique: false });
                    DebugLogger.log('init', 'Created location positions store with timestamp index');
                }
            };
        });
    }

    /**
     * Close the database connection
     */
    close(): void {
        if (this.db) {
            this.db.close();
            this.db = null;
            this.isOpen = false;
        }
    }

    /**
     * Store a single position
     */
    async store(position: GeoPosition): Promise<number> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);

            const record: StoredPosition = { ...position };
            const request = store.add(record);

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to store position', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                const id = request.result as number;
                DebugLogger.log('storage', 'Position stored', {
                    id,
                    lat: position.latitude.toFixed(6),
                    lon: position.longitude.toFixed(6),
                    accuracy: position.accuracy
                });
                resolve(id);
            };
        });
    }

    /**
     * Store a position unless one with the same timestamp is already stored (the Android
     * app may deliver a fix again when a navigation cut off its acknowledgement).
     * Returns true if it was stored.
     */
    async storeIfNew(position: GeoPosition): Promise<boolean> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const count = store.index('timestamp').count(position.timestamp);
            let stored = false;

            count.onsuccess = () => {
                if (count.result > 0) return;
                const record: StoredPosition = { ...position };
                store.add(record).onsuccess = () => {
                    stored = true;
                };
            };
            transaction.oncomplete = () => resolve(stored);
            transaction.onerror = () => {
                DebugLogger.log('error', 'Failed to store position', { error: transaction.error?.message });
                reject(transaction.error);
            };
        });
    }

    /**
     * Query positions within a time range
     */
    async query(startTime: number, endTime: number): Promise<GeoPosition[]> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');

            const range = IDBKeyRange.bound(startTime, endTime);
            const request = index.getAll(range);

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to query positions', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                const positions = request.result as StoredPosition[];
                // Remove the internal id field before returning
                const result = positions.map(({ id: _id, ...pos }) => pos as GeoPosition);
                DebugLogger.log('storage', 'Query returned positions', {
                    count: result.length,
                    startTime: new Date(startTime).toISOString(),
                    endTime: new Date(endTime).toISOString()
                });
                resolve(result);
            };
        });
    }

    /**
     * Get the latest stored position
     */
    async getLatest(): Promise<GeoPosition | null> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');

            // Open cursor in reverse order (newest first)
            const request = index.openCursor(null, 'prev');

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to get latest position', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                const cursor = request.result;
                if (cursor) {
                    const stored = cursor.value as StoredPosition;
                    const { id: _id, ...position } = stored;
                    resolve(position as GeoPosition);
                } else {
                    resolve(null);
                }
            };
        });
    }

    /**
     * Get total count of stored positions
     */
    async getCount(): Promise<number> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readonly');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.count();

            request.onerror = () => reject(request.error);
            request.onsuccess = () => resolve(request.result);
        });
    }

    /**
     * Delete positions older than a given timestamp
     */
    async deleteOlderThan(timestamp: number): Promise<number> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const index = store.index('timestamp');

            // Get all positions older than timestamp
            const range = IDBKeyRange.upperBound(timestamp, true);
            const request = index.openCursor(range);

            let deleteCount = 0;

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to delete old positions', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                const cursor = request.result;
                if (cursor) {
                    cursor.delete();
                    deleteCount++;
                    cursor.continue();
                } else {
                    DebugLogger.log('storage', 'Deleted old positions', {
                        count: deleteCount,
                        olderThan: new Date(timestamp).toISOString()
                    });
                    resolve(deleteCount);
                }
            };
        });
    }

    /**
     * Clear all stored positions
     */
    async clear(): Promise<void> {
        if (!this.db) {
            await this.open();
        }

        return new Promise((resolve, reject) => {
            const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
            const store = transaction.objectStore(STORE_NAME);
            const request = store.clear();

            request.onerror = () => {
                DebugLogger.log('error', 'Failed to clear positions', { error: request.error?.message });
                reject(request.error);
            };

            request.onsuccess = () => {
                DebugLogger.log('storage', 'All positions cleared');
                resolve();
            };
        });
    }

    /**
     * Query positions with LTTB downsampling
     *
     * Uses the Largest Triangle Three Buckets algorithm to reduce
     * the number of points while preserving the overall shape of the data.
     *
     * @param startTime - Start of time range (inclusive)
     * @param endTime - End of time range (inclusive)
     * @param maxPoints - Maximum number of points to return
     */
    async queryDownsampled(startTime: number, endTime: number, maxPoints: number): Promise<GeoPosition[]> {
        // First, get all positions in the range
        const allPositions = await this.query(startTime, endTime);

        // If we have fewer points than max, return all
        if (allPositions.length <= maxPoints || maxPoints < 3) {
            return allPositions;
        }

        // Apply LTTB downsampling using latitude as the primary value
        // (could be extended to consider longitude as well)
        return this.lttbDownsample(allPositions, maxPoints);
    }

    /**
     * LTTB (Largest Triangle Three Buckets) downsampling algorithm
     *
     * This algorithm selects points that preserve the visual shape of the data
     * when plotted. It works by dividing the data into buckets and selecting
     * the point from each bucket that forms the largest triangle with the
     * selected points from adjacent buckets.
     */
    private lttbDownsample(data: GeoPosition[], targetPoints: number): GeoPosition[] {
        if (data.length <= targetPoints) {
            return data;
        }

        const sampled: GeoPosition[] = [];

        // Always keep the first point
        sampled.push(data[0]);

        // Bucket size (excluding first and last points)
        const bucketSize = (data.length - 2) / (targetPoints - 2);

        let a = 0; // Index of previously selected point

        for (let i = 0; i < targetPoints - 2; i++) {
            // Calculate bucket boundaries
            const bucketStart = Math.floor((i + 0) * bucketSize) + 1;
            const bucketEnd = Math.floor((i + 1) * bucketSize) + 1;

            // Calculate average point of next bucket (for triangle area calculation)
            const nextBucketStart = Math.floor((i + 1) * bucketSize) + 1;
            const nextBucketEnd = Math.min(
                Math.floor((i + 2) * bucketSize) + 1,
                data.length
            );

            let avgX = 0;
            let avgY = 0;
            let avgCount = 0;

            for (let j = nextBucketStart; j < nextBucketEnd; j++) {
                avgX += data[j].timestamp;
                avgY += data[j].latitude;
                avgCount++;
            }

            avgX /= avgCount;
            avgY /= avgCount;

            // Find the point in current bucket that creates largest triangle
            let maxArea = -1;
            let maxAreaIndex = bucketStart;

            const pointAX = data[a].timestamp;
            const pointAY = data[a].latitude;

            for (let j = bucketStart; j < bucketEnd && j < data.length; j++) {
                // Calculate triangle area using the formula:
                // Area = |Ax(By - Cy) + Bx(Cy - Ay) + Cx(Ay - By)| / 2
                const area = Math.abs(
                    (pointAX - avgX) * (data[j].latitude - pointAY) -
                    (pointAX - data[j].timestamp) * (avgY - pointAY)
                );

                if (area > maxArea) {
                    maxArea = area;
                    maxAreaIndex = j;
                }
            }

            sampled.push(data[maxAreaIndex]);
            a = maxAreaIndex;
        }

        // Always keep the last point
        sampled.push(data[data.length - 1]);

        return sampled;
    }
}

// Singleton instance
let storageInstance: LocationStorage | null = null;

/**
 * Get the singleton LocationStorage instance
 */
export function getLocationStorage(): LocationStorage {
    if (!storageInstance) {
        storageInstance = new LocationStorage();
    }
    return storageInstance;
}
